import { useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { AdminProduct, AdminProductDetail, Page, ProductLine } from '@popcart/core/v1';
import { api, errorMessage } from '../api';
import { useToast } from '../ui/toast';
import { patchPages, restoreToast, visibilityToast } from './lib/list.js';
import { catalogKeys } from './keys';

type Pages = InfiniteData<Page<AdminProduct>, string | undefined>;

/** Active categories of one line, for the editor's picker. */
export function useCategories(line: ProductLine) {
  return useQuery({ queryKey: catalogKeys.categories(line), queryFn: () => api.categories(line), staleTime: 60_000 });
}

/** After any product change: lists, details and the shop's own stock/today views refresh. */
export function useInvalidateProduct() {
  const qc = useQueryClient();
  return (shopId: string) => {
    void qc.invalidateQueries({ queryKey: catalogKeys.all });
    void qc.invalidateQueries({ queryKey: ['shop', shopId] });
  };
}

/** Patch a product in every cached list (optimistic), returning a rollback. */
function usePatchLists() {
  const qc = useQueryClient();
  return async (id: string, fn: (p: AdminProduct) => AdminProduct | null) => {
    await qc.cancelQueries({ queryKey: catalogKeys.lists });
    const prev = qc.getQueriesData<Pages>({ queryKey: catalogKeys.lists });
    qc.setQueriesData<Pages>({ queryKey: catalogKeys.lists }, (d) => patchPages(d, id, fn) as Pages | undefined);
    return () => {
      for (const [k, v] of prev) qc.setQueryData(k, v);
    };
  };
}

/** Hide/show from the list or the editor. */
export function useSetVisible() {
  const qc = useQueryClient();
  const toast = useToast();
  const patch = usePatchLists();
  const invalidate = useInvalidateProduct();
  return useMutation({
    mutationFn: (v: { product: Pick<AdminProduct, 'id' | 'shopId' | 'name'>; visible: boolean }) => api.setProductVisible(v.product.shopId, v.product.id, v.visible),
    onMutate: async (v) => ({ rollback: await patch(v.product.id, (p) => ({ ...p, status: v.visible ? 'active' : 'hidden' })) }),
    onError: (err, _v, ctx) => {
      ctx?.rollback();
      toast(errorMessage(err, "Couldn't change that. Try again."), 'error');
    },
    onSuccess: (d, v) => {
      qc.setQueryData<AdminProductDetail>(catalogKeys.product(d.shopId, d.id), d);
      toast(visibilityToast(v.product, v.visible));
    },
    onSettled: (_d, _e, v) => invalidate(v.product.shopId),
  });
}

/** Archived → hidden (contract: restore brings it back hidden). */
export function useRestore() {
  const qc = useQueryClient();
  const toast = useToast();
  const invalidate = useInvalidateProduct();
  return useMutation({
    mutationFn: (p: Pick<AdminProduct, 'id' | 'shopId' | 'name'>) => api.restoreProduct(p.shopId, p.id),
    onError: (err) => toast(errorMessage(err, "Couldn't restore it. Try again."), 'error'),
    onSuccess: (d, p) => {
      qc.setQueryData<AdminProductDetail>(catalogKeys.product(d.shopId, d.id), d);
      toast(restoreToast(p));
    },
    onSettled: (_d, _e, p) => invalidate(p.shopId),
  });
}

/** Restock / correction through the existing stock endpoint. */
export function useAdjustStock() {
  const invalidate = useInvalidateProduct();
  return useMutation({
    mutationFn: (v: { shopId: string; productId: string; delta: number; reason: 'restock' | 'adjust'; note?: string }) =>
      api.adjustStock(v.shopId, v.productId, v.delta, v.reason, v.note),
    onSettled: (_d, _e, v) => invalidate(v.shopId),
  });
}
